/**
 * Email Service - Frontend
 *
 * This service communicates with Firebase Cloud Functions to send emails securely.
 * The SendGrid API key is never exposed to the client.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../config/firebase';

const FUNCTIONS_REGION = import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION || 'us-central1';

function getFunctionsClient() {
  return getFunctions(app, FUNCTIONS_REGION);
}

function normalizeFunctionsCode(rawCode) {
  if (typeof rawCode !== 'string') return '';
  return rawCode.startsWith('functions/') ? rawCode.replace('functions/', '') : rawCode;
}

function friendlyAuthErrorForCode(code) {
  switch (code) {
    case 'not-found':
      return 'No events found for this email address.';
    case 'permission-denied':
      return 'Invalid password. Please try again.';
    case 'invalid-argument':
      return 'Please enter a valid email and password.';
    case 'unauthenticated':
      return 'Your session is no longer valid. Please log in again.';
    case 'unavailable':
      return 'Login service is temporarily unavailable. Please try again shortly.';
    default:
      return null;
  }
}

function toFriendlyClientAuthError(error) {
  const code = normalizeFunctionsCode(error?.code);
  const message = typeof error?.message === 'string' ? error.message.toLowerCase() : '';

  const friendlyByCode = friendlyAuthErrorForCode(code);
  if (friendlyByCode) return friendlyByCode;

  if (
    message.includes('cors') ||
    message.includes('preflight') ||
    message.includes('access-control-allow-origin')
  ) {
    return 'Login service is blocked by CORS configuration. Please redeploy functions and try again.';
  }

  if (message.includes('failed to fetch') || message.includes('networkerror')) {
    return 'Unable to reach login service. Please check your connection and try again.';
  }

  if (error?.message && error.message !== 'internal') {
    return error.message;
  }

  return 'Authentication failed due to a server issue. Please try again.';
}

async function verifyClientCredentialsViaHttp(email, password) {
  const projectId = app?.options?.projectId;

  if (!projectId) {
    return { success: false, error: 'Firebase project configuration is missing.' };
  }

  const endpoint = `https://${FUNCTIONS_REGION}-${projectId}.cloudfunctions.net/verifyClientCredentialsHttp`;

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password })
    });

    const payload = await response.json().catch(() => ({}));
    const code = normalizeFunctionsCode(payload?.code);
    const friendlyByCode = friendlyAuthErrorForCode(code);

    if (!response.ok || !payload?.success) {
      return {
        success: false,
        error: friendlyByCode || payload?.error || `Authentication failed (${response.status}). Please try again.`
      };
    }

    return payload;
  } catch (error) {
    return { success: false, error: toFriendlyClientAuthError(error) };
  }
}

/**
 * Validates email address format
 */
export function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
}

/**
 * Parses and validates email addresses (client-side preview)
 */
export function parseAndValidateEmails(emailInput) {
  // Split by common delimiters
  const rawEmails = emailInput.split(/[,;\n\r]+/);

  const validEmails = [];
  const invalidEmails = [];

  for (const email of rawEmails) {
    const trimmedEmail = email.trim();

    // Skip empty strings
    if (trimmedEmail.length === 0) continue;

    if (isValidEmail(trimmedEmail)) {
      // Avoid duplicates
      const normalizedEmail = trimmedEmail.toLowerCase();
      if (!validEmails.includes(normalizedEmail)) {
        validEmails.push(normalizedEmail);
      }
    } else {
      invalidEmails.push(trimmedEmail);
    }
  }

  return {
    valid: validEmails,
    invalid: invalidEmails
  };
}

/**
 * Sends gallery link email by calling Firebase Cloud Function
 *
 * @param {Object} eventData - Event information
 * @param {string} recipients - Comma/newline separated email addresses
 * @param {string} emailSubject - Custom email subject line (optional)
 * @returns {Promise<Object>} Result object with success status
 */
export async function sendGalleryLinkEmail(eventData, recipients, emailSubject = null) {
  try {
    // Get reference to the Cloud Function
    const sendGalleryEmail = httpsCallable(getFunctionsClient(), 'sendGalleryEmail');

    // Call the Cloud Function
    const result = await sendGalleryEmail({
      eventData: {
        id: eventData.id,
        eventName: eventData.eventName,
        eventType: eventData.eventType,
        eventDate: eventData.eventDate,
        location: eventData.location,
        hostName: eventData.hostName,
        guestPassword: eventData.guestPassword || null,
        videoCount: eventData.videoCount || 0
      },
      recipients: recipients,
      emailSubject: emailSubject
    });

    // Return the result from the Cloud Function
    return result.data;

  } catch (error) {
    console.error('Error calling sendGalleryEmail function:', error);

    // Handle specific error codes
    if (error.code === 'unauthenticated') {
      return {
        success: false,
        error: 'You must be logged in as an admin to send emails.'
      };
    }

    if (error.code === 'invalid-argument') {
      return {
        success: false,
        error: error.message
      };
    }

    // Generic error
    return {
      success: false,
      error: error.message || 'Failed to send email. Please try again.'
    };
  }
}

/**
 * Sends client setup email by calling Firebase Cloud Function
 */
export async function sendClientSetupEmail(eventId) {
  try {
    const callable = httpsCallable(getFunctionsClient(), 'sendClientSetupEmail');
    const result = await callable({ eventId });
    return result.data;
  } catch (error) {
    console.error('Error calling sendClientSetupEmail:', error);
    return { success: false, error: error.message || 'Failed to send setup email.' };
  }
}

/**
 * Sets up client password via Cloud Function (unauthenticated)
 */
export async function setupClientPassword({ eventId, token, password }) {
  try {
    const callable = httpsCallable(getFunctionsClient(), 'setupClientPassword');
    const result = await callable({ eventId, token, password });
    return result.data;
  } catch (error) {
    console.error('Error calling setupClientPassword:', error);
    return { success: false, error: error.message || 'Failed to set up password.' };
  }
}

/**
 * Deletes videos as client via Cloud Function (unauthenticated)
 */
export async function deleteVideosAsClient(eventId, clientPassword, videoIds) {
  try {
    const callable = httpsCallable(getFunctionsClient(), 'deleteVideosAsClient');
    const result = await callable({ eventId, clientPassword, videoIds });
    return result.data;
  } catch (error) {
    console.error('Error calling deleteVideosAsClient:', error);
    return { success: false, error: error.message || 'Failed to delete videos.' };
  }
}

/**
 * Gets events associated with a client email (DEPRECATED - use verifyClientCredentials)
 */
export async function getClientEvents(email) {
  try {
    const callable = httpsCallable(getFunctionsClient(), 'getClientEvents');
    const result = await callable({ email });
    return result.data;
  } catch (error) {
    console.error('Error calling getClientEvents:', error);
    return { success: false, error: error.message || 'Failed to find events.' };
  }
}

/**
 * Verifies client credentials (email + password) and returns events
 */
export async function verifyClientCredentials(email, password) {
  try {
    const callable = httpsCallable(getFunctionsClient(), 'verifyClientCredentials');
    const result = await callable({ email, password });
    return result.data;
  } catch (error) {
    console.error('Error calling verifyClientCredentials:', error);

    const code = normalizeFunctionsCode(error?.code);
    const message = typeof error?.message === 'string' ? error.message.toLowerCase() : '';
    const shouldUseHttpFallback =
      code === 'internal' ||
      code === 'unavailable' ||
      message.includes('cors') ||
      message.includes('preflight') ||
      message.includes('failed to fetch');

    if (shouldUseHttpFallback) {
      const fallbackResult = await verifyClientCredentialsViaHttp(email, password);
      if (fallbackResult.success) {
        return fallbackResult;
      }
      return fallbackResult;
    }

    return { success: false, error: toFriendlyClientAuthError(error) };
  }
}

/**
 * Requests a client password reset email
 */
export async function requestClientPasswordReset(email) {
  try {
    const callable = httpsCallable(getFunctionsClient(), 'requestClientPasswordReset');
    const result = await callable({ email });
    return result.data;
  } catch (error) {
    console.error('Error calling requestClientPasswordReset:', error);
    // Always return success to match backend behavior (prevent enumeration)
    return { success: true };
  }
}

/**
 * Resets client password using a reset token
 */
export async function resetClientPassword({ email, token, password }) {
  try {
    const callable = httpsCallable(getFunctionsClient(), 'resetClientPassword');
    const result = await callable({ email, token, password });
    return result.data;
  } catch (error) {
    console.error('Error calling resetClientPassword:', error);

    const code = normalizeFunctionsCode(error?.code);

    if (code === 'permission-denied' || code === 'not-found') {
      return { success: false, error: 'Invalid or expired reset link. Please request a new one.' };
    }

    if (code === 'invalid-argument') {
      return { success: false, error: error.message || 'Please provide a valid password.' };
    }

    return { success: false, error: error.message || 'Failed to reset password. Please try again.' };
  }
}

/**
 * Sends gallery link email as an authenticated client
 *
 * @param {string} eventId - Event ID to share
 * @param {string} clientEmail - Client's email address
 * @param {string} clientPassword - Client's password
 * @param {string} recipients - Comma/newline separated email addresses
 * @returns {Promise<Object>} Result object with success status
 */
export async function sendGalleryLinkEmailAsClient(eventId, clientEmail, clientPassword, recipients) {
  try {
    const callable = httpsCallable(getFunctionsClient(), 'sendGalleryEmailAsClient');
    const result = await callable({
      eventId,
      clientEmail,
      clientPassword,
      recipients
    });
    return result.data;
  } catch (error) {
    console.error('Error calling sendGalleryEmailAsClient:', error);

    const code = normalizeFunctionsCode(error?.code);

    // Provide user-friendly error messages
    if (code === 'permission-denied') {
      return {
        success: false,
        error: 'Invalid password or you do not have permission to share this event.'
      };
    }

    if (code === 'invalid-argument') {
      return {
        success: false,
        error: error.message || 'Please provide valid email addresses.'
      };
    }

    if (code === 'not-found') {
      return {
        success: false,
        error: 'Event not found.'
      };
    }

    return {
      success: false,
      error: error.message || 'Failed to send email. Please try again.'
    };
  }
}

/**
 * Updates event privacy setting (public/private) as an authenticated client
 *
 * @param {string} eventId - Event ID to update
 * @param {string} clientEmail - Client's email address
 * @param {string} clientPassword - Client's password
 * @param {boolean} isPublic - New privacy setting (true = public, false = private)
 * @returns {Promise<Object>} Result object with success status
 */
export async function updateEventPrivacy(eventId, clientEmail, clientPassword, isPublic) {
  try {
    const callable = httpsCallable(getFunctionsClient(), 'updateEventPrivacy');
    const result = await callable({
      eventId,
      clientEmail,
      clientPassword,
      isPublic
    });
    return result.data;
  } catch (error) {
    console.error('Error calling updateEventPrivacy:', error);

    const code = normalizeFunctionsCode(error?.code);

    // Provide user-friendly error messages
    if (code === 'permission-denied') {
      return {
        success: false,
        error: 'Invalid password or you do not have permission to modify this event.'
      };
    }

    if (code === 'not-found') {
      return {
        success: false,
        error: 'Event not found.'
      };
    }

    return {
      success: false,
      error: error.message || 'Failed to update privacy setting. Please try again.'
    };
  }
}
